import api from './api';
import documents from '../data/legalDocuments.json';
import { accountId, assertCurrentSession } from '../utils/authSession';
import { canLogInToWeb } from '../utils/roles';

export const TERMS_HASH = documents.terms_of_service.textSha256;
export const PRIVACY_HASH = documents.privacy_policy.textSha256;
const HASH = /^[a-f0-9]{64}$/;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/;
const invalid = () => new Error('We could not verify your acceptance status. Please try again.');
const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function isTimestamp(value) {
  if (typeof value !== 'string' || !ISO.test(value) || !Number.isFinite(Date.parse(value))) return false;
  // Date.parse normalizes impossible dates such as February 30; a receipt must be well formed.
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1] && Number(value.slice(11, 13)) < 24;
}

function unwrap(response) {
  if (!object(response?.data) || response.data.success !== true || !object(response.data.data)) throw invalid();
  return response.data.data;
}

export function validateReceipt(receipt, user) {
  const id = accountId(user);
  if (!id || !object(receipt) || receipt.userId !== id ||
      typeof receipt.accepted !== 'boolean' || typeof receipt.exempt !== 'boolean' ||
      receipt.exempt !== (user.role === 'admin') ||
      typeof receipt.termsHash !== 'string' || typeof receipt.privacyHash !== 'string' ||
      !HASH.test(receipt.termsHash) || !HASH.test(receipt.privacyHash)) throw invalid();
  const validDate = isTimestamp(receipt.acceptedAt);
  if (receipt.acceptedAt !== null && !validDate) throw invalid();
  if (receipt.accepted ? !validDate : receipt.acceptedAt !== null) throw invalid();
  return {
    userId: id, accepted: receipt.accepted, exempt: receipt.exempt,
    acceptedAt: receipt.acceptedAt, termsHash: receipt.termsHash, privacyHash: receipt.privacyHash,
  };
}

export function documentsMatch(receipt) {
  return receipt.termsHash === TERMS_HASH && receipt.privacyHash === PRIVACY_HASH;
}

const options = (session, signal) => ({
  sessionBinding: session, signal, timeout: 20000,
  headers: { 'Cache-Control': 'no-store' },
});

const legalService = {
  async getProfile(session, signal) {
    assertCurrentSession(session);
    const response = await api.get('/user/profile', options(session, signal));
    assertCurrentSession(session);
    const profile = unwrap(response);
    if (accountId(profile) !== session.userId || !accountId(profile) || !canLogInToWeb(profile.role)) throw invalid();
    return profile;
  },
  async getStatus(user, session, signal) {
    assertCurrentSession(session);
    const response = await api.get('/legal/status', options(session, signal));
    assertCurrentSession(session);
    return validateReceipt(unwrap(response), user);
  },
  async accept(user, session, signal) {
    assertCurrentSession(session);
    const response = await api.post('/legal/acceptance', {
      termsAccepted: true, privacyAccepted: true, termsHash: TERMS_HASH, privacyHash: PRIVACY_HASH,
    }, { ...options(session, signal), skipAuthReplay: true });
    assertCurrentSession(session);
    const receipt = validateReceipt(unwrap(response), user);
    if (!receipt.accepted && !receipt.exempt) throw invalid();
    return receipt;
  },
};

export default legalService;
